import React, { useState, useEffect, useMemo } from 'react';
import {
    dashboardApi,
    WctDashboardResponse,
    WctMissingMapping,
    WctTransaction,
    WctMissingTransaction,
    WctCategory,
    UnmatchedConversion
} from '../services/dashboardApi';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, ComposedChart, Bar, Line } from 'recharts';
import { Activity, DollarSign, TrendingUp, AlertTriangle, Layers, Save, Filter, ChevronRight, ChevronLeft, Tag, Plus } from 'lucide-react';
import MainLayout from '../components/layout/MainLayout';

const MetricBox = ({ title, value, yesterday, today, diff }: any) => {
    const isPositive = diff > 0;
    const isNegative = diff < 0;
    return (
        <div className="bg-white py-4 flex flex-col items-center justify-center relative p-2 h-full w-full">
            <div className="text-gray-600 font-semibold mb-2 text-xs sm:text-sm text-center">{title}</div>
            <div className="text-xl sm:text-3xl font-normal text-gray-800 mb-1 text-center font-display w-full px-2 truncate leading-none">{value}</div>

            <div className={`text-xs sm:text-sm font-medium ${isPositive ? 'text-green-500' : isNegative ? 'text-red-500' : 'text-gray-400'} flex items-center justify-center mb-4`}>
                {isPositive ? '↑' : isNegative ? '↓' : ''} {diff !== 0 ? Math.abs(diff).toLocaleString('de-DE') : ''}
            </div>

            <div className="w-full flex justify-between text-[10px] sm:text-[11px] text-gray-400 border-t border-gray-100 pt-3 px-2 mt-auto">
                <div className="flex gap-1 items-center">
                    <span className="hidden xl:inline">Yesterday</span><span className="xl:hidden">Yest.</span>
                    <span className="text-gray-600 font-medium">{yesterday}</span>
                </div>
                <div className="flex gap-1 items-center">
                    <span>Today</span>
                    <span className="text-gray-600 font-medium">{today}</span>
                </div>
            </div>
        </div>
    );
};

// Format helper globally available
const formatDKK = (cents: number | string) => {
    const value = Number(cents) / 100;
    return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export default function MainDashboard() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Data State
    const [dashboardData, setDashboardData] = useState<WctDashboardResponse | null>(null);
    const [transactions, setTransactions] = useState<WctTransaction[]>([]);
    const [missingMappings, setMissingMappings] = useState<WctMissingMapping[]>([]);
    const [missingTransactions, setMissingTransactions] = useState<WctMissingTransaction[]>([]);
    const [unmatchedConversions, setUnmatchedConversions] = useState<UnmatchedConversion[]>([]);
    const [categories, setCategories] = useState<WctCategory[]>([]);
    const [editingUnmatched, setEditingUnmatched] = useState<number | null>(null);
    const [unmatchedLeadId, setUnmatchedLeadId] = useState('');

    // Filters
    const [geoFilter, setGeoFilter] = useState('all');
    const [espFilter, setEspFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('7'); // days or 'custom'
    const [showPending, setShowPending] = useState(false);
    const [latestDataDate, setLatestDataDate] = useState<string | null>(null);
    const [autoExpanded, setAutoExpanded] = useState(false);
    const [actualDays, setActualDays] = useState(7);
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');

    // Editing States
    const [editingMapping, setEditingMapping] = useState<string | null>(null);
    const [mappingForm, setMappingForm] = useState({ geo: 'no', campaign_name: '', category: '' });
    const [campaigns, setCampaigns] = useState<any[]>([]);
    const [selectedCampaignId, setSelectedCampaignId] = useState('');

    const [editingTransaction, setEditingTransaction] = useState<string | null>(null);
    const [transactionForm, setTransactionForm] = useState({ esp: '' });

    // Category Creation
    const [newCategoryName, setNewCategoryName] = useState('');

    // Pagination (Simple client-side for Transactions)
    const [txPage, setTxPage] = useState(1);
    const TX_PER_PAGE = 10;
    const [txLoading, setTxLoading] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        setError('');
        let daysNum: number;
        let startDateParam: string | undefined;
        let endDateParam: string | undefined;

        if (dateFilter === 'custom' && customStart) {
            startDateParam = customStart;
            endDateParam = customEnd || customStart;
            const startDate = new Date(customStart);
            const endDate = new Date(endDateParam);
            daysNum = Math.max(1, Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1);
        } else {
            daysNum = parseInt(dateFilter) || 7;
        }

        const geo = geoFilter !== 'all' ? geoFilter : undefined;
        const esp = espFilter !== 'all' ? espFilter : undefined;

        // 1. Fetch Primary Dashboard metrics FIRST (KPIs, Charts, Advertiser Report)
        try {
            const dashRes = await dashboardApi.getWctDashboard(geo, esp, daysNum, showPending, startDateParam, endDateParam);
            setDashboardData(dashRes);
            setLatestDataDate(dashRes.latest_data_date || null);
            setAutoExpanded(dashRes.auto_expanded || false);
            setActualDays(dashRes.actual_days || parseInt(dateFilter) || 7);
        } catch (err: any) {
            setError(err.message || 'Failed to fetch dashboard data');
        } finally {
            setLoading(false);
        }

        // 2. Fetch Secondary Data (Transactions, Mappings, Campaigns) in parallel without holding up the main dashboard
        setTxLoading(true);
        Promise.all([
            dashboardApi.getWctTransactions(geo, esp, 500, showPending, daysNum, startDateParam, endDateParam).catch(() => ({ data: [] })),
            dashboardApi.getWctMissingMappings().catch(() => ({ missing: [] })),
            dashboardApi.getWctMissingTransactions().catch(() => ({ missing_transactions: [] })),
            dashboardApi.getWctCategories().catch(() => ({ categories: [] })),
            dashboardApi.getCampaigns().catch(() => ({ campaigns: [] })),
            dashboardApi.getUnmatchedConversions().catch(() => ({ unmatched: [] }))
        ]).then(([txRes, missingMapRes, missingTxRes, catRes, campaignsRes, unmatchedRes]) => {
            setTransactions(txRes.data || []);
            setMissingMappings(missingMapRes.missing || []);
            setMissingTransactions(missingTxRes.missing_transactions || []);
            setCategories(catRes.categories || []);
            setCampaigns(campaignsRes.campaigns || []);
            setUnmatchedConversions(unmatchedRes.unmatched || []);

            if (catRes.categories && catRes.categories.length > 0 && !mappingForm.category) {
                setMappingForm(prev => ({ ...prev, category: catRes.categories[0].name }));
            }
        }).catch((err) => {
            console.error('Failed to fetch secondary dashboard data', err);
        }).finally(() => {
            setTxLoading(false);
        });
    };

    useEffect(() => {
        // For custom date, only fetch when both dates are set (or at least start)
        if (dateFilter === 'custom' && !customStart) return;
        fetchData();
        setTxPage(1); // reset pagination on filter change
    }, [geoFilter, espFilter, dateFilter, showPending, customStart, customEnd]);

    // Handlers: Mapping
    const handleEditMapping = (advName: string) => {
        setEditingMapping(advName);
        setSelectedCampaignId(campaigns.length > 0 ? campaigns[0].campaignId : '');
    };

    const handleSaveMapping = async (advName: string) => {
        try {
            if (!selectedCampaignId) {
                alert("Please select a campaign first.");
                return;
            }
            await dashboardApi.upsertWctMapping({ advertiser_name: advName, campaign_id: selectedCampaignId });
            setEditingMapping(null);
            fetchData();
        } catch (err: any) {
            alert(`Error saving mapping: ${err.message}`);
        }
    };

    // Handlers: Transaction Override
    const handleEditTransaction = (txId: string) => {
        setEditingTransaction(txId);
        setTransactionForm({ esp: '' });
    };

    const handleSaveTransaction = async (txId: string) => {
        try {
            await dashboardApi.upsertTransactionOverride({ transaction_id: txId, esp: transactionForm.esp });
            setEditingTransaction(null);
            fetchData();
        } catch (err: any) {
            alert(`Error saving transaction esp: ${err.message}`);
        }
    };

    // Handlers: Category
    const handleCreateCategory = async () => {
        if (!newCategoryName.trim()) return;
        try {
            await dashboardApi.createWctCategory(newCategoryName);
            setNewCategoryName('');
            // Refresh just the categories
            const catRes = await dashboardApi.getWctCategories();
            setCategories(catRes.categories || []);
        } catch (err: any) {
            alert(`Error creating category: ${err.message}`);
        }
    };

    // Derived Variables
    const kpis = dashboardData?.kpis || { total_conversions: 0, total_revenue: 0, approved_conversions: 0 };
    const approvalRate = kpis.total_conversions > 0
        ? ((kpis.approved_conversions / kpis.total_conversions) * 100).toFixed(1)
        : '0';

    // Format chart data (Pivot status into separate keys for Recharts AreaChart)
    const chartData = useMemo(() => {
        if (!dashboardData?.chart_data) return [];
        const dateMap: Record<string, any> = {};
        dashboardData.chart_data.forEach(d => {
            if (!dateMap[d.date]) dateMap[d.date] = { date: d.date, approved: 0, pending: 0, declined: 0, sales: 0 };

            const statusKey = d.status.toLowerCase();
            // Group other statuses if necessary
            // Note: Chart displays value directly, so dividing by 100
            const floatComm = Number(d.commission) / 100;
            if (['approved', 'paid'].includes(statusKey)) dateMap[d.date].approved += floatComm;
            else if (['pending'].includes(statusKey)) dateMap[d.date].pending += floatComm;
            else dateMap[d.date].declined += floatComm;

            dateMap[d.date].sales += Number(d.conversions) || 0;
        });
        return Object.values(dateMap).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }, [dashboardData]);

    const paginatedTransactions = useMemo(() => {
        const start = (txPage - 1) * TX_PER_PAGE;
        return transactions.slice(start, start + TX_PER_PAGE);
    }, [transactions, txPage]);

    const totalTxPages = Math.ceil(transactions.length / TX_PER_PAGE) || 1;

    // Today/Yesterday stats (computed server-side in Copenhagen timezone)
    const todayStats = dashboardData?.today || { conversions: 0, commission: 0 };
    const yesterdayStats = dashboardData?.yesterday || { conversions: 0, commission: 0 };

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in pb-12">

                {/* Header & Filters */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div>
                        <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-teal-600 to-indigo-600">
                            WeCanTrack Publisher Dashboard
                        </h2>
                        <p className="text-gray-500 mt-1">Real-time performance metrics and traffic allocation</p>
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-white p-2 rounded-xl border border-gray-200 shadow-sm w-full md:w-auto">
                        <select
                            className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block w-full p-2.5"
                            value={dateFilter}
                            onChange={(e) => {
                                setDateFilter(e.target.value);
                                if (e.target.value !== 'custom') {
                                    setCustomStart('');
                                    setCustomEnd('');
                                }
                            }}
                        >
                            <option value="7">Last 7 Days</option>
                            <option value="30">Last 30 Days</option>
                            <option value="90">Last 90 Days</option>
                            <option value="365">Last 365 Days</option>
                            <option value="custom">Custom Range</option>
                        </select>
                        {dateFilter === 'custom' && (
                            <div className="flex items-center gap-2">
                                <input
                                    type="date"
                                    value={customStart}
                                    onChange={(e) => setCustomStart(e.target.value)}
                                    className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 p-2"
                                    placeholder="Start"
                                />
                                <span className="text-gray-400 text-sm">→</span>
                                <input
                                    type="date"
                                    value={customEnd}
                                    onChange={(e) => setCustomEnd(e.target.value)}
                                    className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 p-2"
                                    placeholder="End"
                                />
                            </div>
                        )}
                        <select
                            className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block w-full p-2.5"
                            value={geoFilter}
                            onChange={(e) => setGeoFilter(e.target.value)}
                        >
                            <option value="all">All GEOs</option>
                            <option value="no">Norway (NO)</option>
                            <option value="se">Sweden (SE)</option>
                            <option value="fi">Finland (FI)</option>
                            <option value="de">Germany (DE)</option>
                        </select>
                        <select
                            className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block w-full p-2.5"
                            value={espFilter}
                            onChange={(e) => setEspFilter(e.target.value)}
                        >
                            <option value="all">All ESPs</option>
                            <option value="gr">GetResponse</option>
                            <option value="fb">Feedblitz</option>
                            <option value="br">Brevo</option>
                            <option value="ml">MailerLite</option>
                            <option value="sl">SimplyLoan</option>
                            <option value="ms">Moosend</option>
                            <option value="dp">Drip</option>
                        </select>
                        <div className="flex items-center gap-2 px-2 sm:border-l border-gray-200 h-full">
                            <input
                                type="checkbox"
                                id="showPending"
                                checked={showPending}
                                onChange={(e) => setShowPending(e.target.checked)}
                                className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500 cursor-pointer"
                            />
                            <label htmlFor="showPending" className="text-sm text-gray-700 whitespace-nowrap cursor-pointer">Show Pending</label>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 text-red-700 p-4 rounded-xl border border-red-200">
                        {error}
                    </div>
                )}

                {/* Stale Data Warning */}
                {autoExpanded && latestDataDate && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-5 py-3 flex items-start gap-3">
                        <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
                        <div>
                            <p className="text-sm font-medium text-amber-800">
                                No conversions in the last {dateFilter} days.
                            </p>
                            <p className="text-xs text-amber-600 mt-0.5">
                                Showing data from the last {actualDays} days instead (latest conversion: {new Date(latestDataDate).toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric'})}). 
                                Check your WeCanTrack webhook integration.
                            </p>
                        </div>
                    </div>
                )}

                {/* Dashboard KPIs */}
                {/* KPI Bar identical to WeCanTrack Design */}
                <div className="rounded-sm shadow-sm border border-gray-200 bg-gray-200 gap-[1px] grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 overflow-hidden w-full">
                    <MetricBox title="Conversions" value={kpis.total_conversions ? kpis.total_conversions.toLocaleString('de-DE') : '0'} diff={0} yesterday={yesterdayStats.conversions} today={todayStats.conversions} />
                    <MetricBox title="Commission" value={formatDKK(kpis.total_revenue)} diff={0} yesterday={formatDKK(yesterdayStats.commission)} today={formatDKK(todayStats.commission)} />
                    <MetricBox title="Sale Amount" value="0,0" diff={0} yesterday={0} today={0} />
                    <MetricBox title="Clicks" value="0" diff={0} yesterday={0} today={0} />
                    <MetricBox title="Sessions" value="0" diff={0} yesterday={0} today={0} />
                    <MetricBox title="CTR" value="0,00 %" diff={0} yesterday={0} today={0} />
                    <MetricBox title="ECPC" value="0" diff={0} yesterday={0} today={0} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
                    {/* Sessions, Clicks & Sales Bar/Line Composed Chart */}
                    <div className="bg-white rounded-sm shadow-sm border border-gray-200 p-4 sm:p-6 flex flex-col h-[300px] sm:h-[350px]">
                        <div className="flex justify-between items-center mb-4 pb-2">
                            <h3 className="text-sm font-semibold text-gray-700">Sessions, Clicks & Sales</h3>
                        </div>
                        <div className="w-full flex-grow">
                            {loading ? <div className="animate-pulse bg-gray-100 h-full w-full rounded"></div> : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart data={chartData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(val) => {
                                            const d = new Date(val);
                                            return `${d.getDate()}. ${d.toLocaleString('en', { month: 'short' })}`;
                                        }} />
                                        <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <Tooltip contentStyle={{ borderRadius: '4px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgb(0 0 0 / 0.05)' }} />
                                        <Legend verticalAlign="top" iconType="square" iconSize={10} wrapperStyle={{ fontSize: '11px', paddingBottom: '20px' }} />
                                        <Bar dataKey="sales" name="Sales" fill="#007f8c" barSize={35} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                    {/* Commission by Status Chart */}
                    <div className="bg-white rounded-sm shadow-sm border border-gray-200 p-4 sm:p-6 flex flex-col h-[300px] sm:h-[350px]">
                        <div className="flex justify-between items-center mb-4 pb-2">
                            <h3 className="text-sm font-semibold text-gray-700">Commission by Status</h3>
                        </div>
                        <div className="w-full flex-grow">
                            {loading ? <div className="animate-pulse bg-gray-100 h-full w-full rounded"></div> : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={chartData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="colorApproved" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#22c55e" stopOpacity={0.2} />
                                                <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                                            </linearGradient>
                                            <linearGradient id="colorPending" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(val) => {
                                            const d = new Date(val);
                                            return `${d.getDate()}. ${d.toLocaleString('en', { month: 'short' })}`;
                                        }} />
                                        <YAxis tickFormatter={(val: any) => val >= 1000 ? `${(val / 1000).toLocaleString('de-DE')} t` : val.toLocaleString('de-DE')} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <Tooltip contentStyle={{ borderRadius: '4px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgb(0 0 0 / 0.05)' }} />
                                        <Legend verticalAlign="top" iconType="plainline" iconSize={16} wrapperStyle={{ fontSize: '11px', paddingBottom: '20px' }} />
                                        <Area type="linear" dataKey="approved" name="approved" stroke="#22c55e" strokeWidth={2} fillOpacity={1} fill="url(#colorApproved)" />
                                        {showPending && (
                                            <Area type="linear" dataKey="pending" name="pending" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#colorPending)" />
                                        )}
                                    </AreaChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                </div>

                {/* Advertiser Report Table */}
                <div className="bg-white rounded-sm shadow-sm border border-gray-200 overflow-hidden mt-6">
                    <div className="border-b border-gray-100 px-4 sm:px-6 py-4 text-center">
                        <h3 className="text-sm font-bold text-gray-800 tracking-wide">Advertiser Report</h3>
                    </div>
                    <div className="overflow-x-auto w-full">
                        <table className="w-full min-w-[500px] text-left text-xs whitespace-nowrap">
                            <thead>
                                <tr className="bg-white text-gray-800 font-bold border-b border-gray-100">
                                    <th className="px-6 py-4">#</th>
                                    <th className="px-6 py-4">Advertiser</th>
                                    <th className="px-6 py-4">Network</th>
                                    <th className="px-6 py-4">Conv.</th>
                                    <th className="px-6 py-4">Commission</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {loading ? (
                                    <tr><td colSpan={5} className="px-6 py-8 text-center"><div className="animate-pulse h-4 bg-gray-200 rounded w-1/4 mx-auto"></div></td></tr>
                                ) : dashboardData?.advertiser_report.map((row, i) => (
                                    <tr key={i} className="hover:bg-gray-50 text-gray-700">
                                        <td className="px-6 py-3">{i + 1}.</td>
                                        <td className="px-6 py-3 font-semibold">{row.advertiser} <span className="text-gray-400 font-normal ml-1">({row.campaign_name})</span></td>
                                        <td className="px-6 py-3">{row.geo ? `Afima (${row.geo.toUpperCase()})` : 'Unknown'}</td>
                                        <td className="px-6 py-3 font-medium">{row.conversions}</td>
                                        <td className="px-6 py-3 font-medium">{formatDKK(row.commission)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Transactions Report Table */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mt-6">
                    <div className="border-b border-gray-100 px-4 sm:px-6 py-4 flex justify-between items-center">
                        <h3 className="text-lg font-bold text-gray-900">Transactions Report</h3>
                        <div className="flex gap-2 text-sm items-center">
                            <span className="text-gray-500 hidden sm:inline">Page {txPage} of {totalTxPages}</span>
                            <span className="text-gray-500 sm:hidden">{txPage}/{totalTxPages}</span>
                            <button disabled={txPage === 1} onClick={() => setTxPage(p => p - 1)} className="p-1 rounded bg-gray-100 disabled:opacity-50"><ChevronLeft size={16} /></button>
                            <button disabled={txPage === totalTxPages} onClick={() => setTxPage(p => p + 1)} className="p-1 rounded bg-gray-100 disabled:opacity-50"><ChevronRight size={16} /></button>
                        </div>
                    </div>
                    <div className="overflow-x-auto w-full">
                        <table className="w-full min-w-[700px] text-left text-sm whitespace-nowrap">
                            <thead>
                                <tr className="bg-gray-50 text-gray-500 font-medium border-b border-gray-100">
                                    <th className="px-6 py-3">Order Date</th>
                                    <th className="px-6 py-3">Transaction ID</th>
                                    <th className="px-6 py-3">Advertiser</th>
                                    <th className="px-6 py-3">ESP (Traffic Source)</th>
                                    <th className="px-6 py-3">sub_id_1</th>
                                    <th className="px-6 py-3">Status</th>
                                    <th className="px-6 py-3 text-right">Commission</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {txLoading ? (
                                    <tr><td colSpan={7} className="px-6 py-8 text-center"><div className="animate-pulse h-4 bg-gray-200 rounded w-1/4 mx-auto"></div></td></tr>
                                ) : paginatedTransactions.map((row, i) => (
                                    <tr key={i} className="hover:bg-gray-50">
                                        <td className="px-6 py-3 text-gray-500">{new Date(row.order_date).toLocaleString()}</td>
                                        <td className="px-6 py-3 font-mono text-xs text-indigo-600">{row.transaction_id}</td>
                                        <td className="px-6 py-3">{row.advertiser}</td>
                                        <td className="px-6 py-3">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                {row.esp || 'unknown'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-3 text-gray-400 text-xs">{row.sub_id_1 || '-'}</td>
                                        <td className="px-6 py-3">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${row.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                {row.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-3 text-right font-semibold">kr {formatDKK(row.commission)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Missing Data Mappings */}
                {missingMappings.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-sm border border-orange-200 overflow-hidden mt-6">
                        <div className="bg-orange-50 border-b border-orange-200 px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <h3 className="text-lg font-bold text-orange-900">Missing Advertiser Mappings</h3>
                            <div className="bg-orange-100 text-orange-800 text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                                {missingMappings.length} Unmapped
                            </div>
                        </div>
                        <div className="overflow-x-auto w-full">
                            <table className="w-full min-w-[800px] text-left text-sm border-collapse">
                                <thead>
                                    <tr className="bg-gray-50 text-gray-500 border-b border-gray-100">
                                        <th className="px-6 py-3 font-semibold">Advertiser Name</th>
                                        <th className="px-6 py-3 font-semibold">Conversions</th>
                                        <th className="px-6 py-3 font-semibold">GEO</th>
                                        <th className="px-6 py-3 font-semibold">Campaign Name</th>
                                        <th className="px-6 py-3 font-semibold">Category</th>
                                        <th className="px-6 py-3 font-semibold text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {missingMappings.slice(0, 10).map((row, i) => {
                                        const isEditing = editingMapping === row.advertiser_name;
                                        const selectedCampaign = campaigns.find(c => c.campaignId === selectedCampaignId);
                                        return (
                                            <tr key={i} className="hover:bg-gray-50">
                                                <td className="px-6 py-3 font-medium text-gray-900">{row.advertiser_name}</td>
                                                <td className="px-6 py-3">{row.conversion_count}</td>
                                                {isEditing ? (
                                                    <>
                                                        <td className="px-6 py-3 text-gray-500 font-semibold">
                                                            {selectedCampaign?.geo?.toUpperCase() || 'Undefined'}
                                                        </td>
                                                        <td className="px-2 py-2">
                                                            {campaigns.length > 0 ? (
                                                                <select 
                                                                    className="w-full text-sm rounded border-gray-300 p-1.5 border bg-white font-medium text-gray-700" 
                                                                    value={selectedCampaignId} 
                                                                    onChange={e => setSelectedCampaignId(e.target.value)}
                                                                >
                                                                    {campaigns.map(c => (
                                                                        <option key={c.campaignId} value={c.campaignId}>
                                                                            {c.name} - {c.advertiser}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            ) : (
                                                                <span className="text-xs text-red-500 font-semibold">No campaigns found</span>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-3 text-gray-500">
                                                            {selectedCampaign?.category || 'Undefined'}
                                                        </td>
                                                        <td className="px-6 py-2 text-right">
                                                            <div className="flex gap-2 justify-end">
                                                                <button 
                                                                    onClick={() => handleSaveMapping(row.advertiser_name)} 
                                                                    className="bg-indigo-600 text-white px-3 py-1.5 rounded transition hover:bg-indigo-700 text-xs font-semibold disabled:opacity-50" 
                                                                    disabled={campaigns.length === 0}
                                                                >
                                                                    Assign to Campaign
                                                                </button>
                                                                <button 
                                                                    onClick={() => setEditingMapping(null)} 
                                                                    className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded transition hover:bg-gray-200 text-xs font-semibold"
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className="px-6 py-3 text-gray-400 italic">Undefined</td>
                                                        <td className="px-6 py-3 text-gray-400 italic">Undefined</td>
                                                        <td className="px-6 py-3 text-gray-400 italic">Undefined</td>
                                                        <td className="px-6 py-3 text-right">
                                                            <button onClick={() => handleEditMapping(row.advertiser_name)} className="text-indigo-600 font-semibold text-xs hover:underline">Assign</button>
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Missing Transactions (ESP overrides) */}
                {missingTransactions.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-sm border border-red-200 overflow-hidden mt-6">
                        <div className="bg-red-50 border-b border-red-200 px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <h3 className="text-lg font-bold text-red-900">Transactions Missing ESP</h3>
                            <div className="bg-red-100 text-red-800 text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                                {missingTransactions.length} Action Needed
                            </div>
                        </div>
                        <div className="overflow-x-auto w-full">
                            <table className="w-full min-w-[800px] text-left text-sm border-collapse">
                                <thead>
                                    <tr className="bg-gray-50 text-gray-500 border-b border-gray-100">
                                        <th className="px-4 py-3 font-semibold">Transaction ID</th>
                                        <th className="px-4 py-3 font-semibold">Advertiser</th>
                                        <th className="px-4 py-3 font-semibold">Network</th>
                                        <th className="px-4 py-3 font-semibold">GEO</th>
                                        <th className="px-4 py-3 font-semibold">Commission</th>
                                        <th className="px-4 py-3 font-semibold">SubIDs</th>
                                        <th className="px-4 py-3 font-semibold">Set ESP</th>
                                        <th className="px-4 py-3 font-semibold text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {missingTransactions.slice(0, 10).map((row, i) => {
                                        const isEditing = editingTransaction === row.transaction_id;
                                        const subIds = [
                                            row.pubidsub1 && `s1: ${row.pubidsub1}`,
                                            row.pubidsub2 && `s2: ${row.pubidsub2}`,
                                            row.raw_sub1 && `raw1: ${row.raw_sub1}`,
                                            row.raw_sub2 && `raw2: ${row.raw_sub2}`,
                                            row.raw_sub3 && `raw3: ${row.raw_sub3}`,
                                            row.raw_sub4 && `raw4: ${row.raw_sub4}`,
                                            row.raw_sub5 && `raw5: ${row.raw_sub5}`,
                                            row.click_id && `click: ${row.click_id}`,
                                        ].filter(Boolean);
                                        return (
                                            <tr key={i} className="hover:bg-gray-50 flex-none overflow-hidden">
                                                <td className="px-4 py-3 font-mono text-xs max-w-[150px] truncate" title={row.transaction_id}>{row.transaction_id}</td>
                                                <td className="px-4 py-3">{row.advertiser}</td>
                                                <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{row.network_id || '—'}</span></td>
                                                <td className="px-4 py-3 font-bold uppercase">{row.geo}</td>
                                                <td className="px-4 py-3 text-emerald-600 font-semibold">kr {formatDKK(row.commission)}</td>
                                                <td className="px-4 py-3 max-w-[250px]">
                                                    {subIds.length > 0 ? (
                                                        <div className="flex flex-wrap gap-1">
                                                            {subIds.map((s, j) => (
                                                                <span key={j} className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-blue-50 text-blue-700 border border-blue-100">{s}</span>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400 italic text-xs">None</span>
                                                    )}
                                                </td>

                                                {isEditing ? (
                                                    <td className="px-2 py-2">
                                                        <input className="w-full text-sm rounded border-gray-300 p-1.5 border" value={transactionForm.esp} onChange={e => setTransactionForm({ esp: e.target.value })} placeholder={`e.g. fb${row.geo}`} />
                                                    </td>
                                                ) : (
                                                    <td className="px-4 py-3 text-gray-400 italic">Unknown</td>
                                                )}

                                                <td className="px-4 py-3 text-right">
                                                    {isEditing ? (
                                                        <button onClick={() => handleSaveTransaction(row.transaction_id)} className="bg-indigo-600 text-white px-3 py-1.5 rounded transition hover:bg-indigo-700 text-xs font-semibold">Save</button>
                                                    ) : (
                                                        <button onClick={() => handleEditTransaction(row.transaction_id)} className="text-indigo-600 font-semibold text-xs hover:underline">Override</button>
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Unmatched Conversions — no lead found */}
                {unmatchedConversions.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-sm border border-amber-200 overflow-hidden mt-6">
                        <div className="bg-amber-50 border-b border-amber-200 px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <h3 className="text-lg font-bold text-amber-900">Unmatched Conversions (No Lead Found)</h3>
                            <div className="bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                                {unmatchedConversions.length} Pending
                            </div>
                        </div>
                        <div className="overflow-x-auto w-full">
                            <table className="w-full min-w-[900px] text-left text-sm border-collapse">
                                <thead>
                                    <tr className="bg-gray-50 text-gray-500 border-b border-gray-100">
                                        <th className="px-4 py-3 font-semibold">Transaction ID</th>
                                        <th className="px-4 py-3 font-semibold">Advertiser</th>
                                        <th className="px-4 py-3 font-semibold">Network</th>
                                        <th className="px-4 py-3 font-semibold">GEO</th>
                                        <th className="px-4 py-3 font-semibold">Revenue</th>
                                        <th className="px-4 py-3 font-semibold">SubIDs</th>
                                        <th className="px-4 py-3 font-semibold">Assign Lead</th>
                                        <th className="px-4 py-3 font-semibold text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {unmatchedConversions.slice(0, 15).map((row) => {
                                        const isEditing = editingUnmatched === row.id;
                                        const subIds = [
                                            row.pubidsub1 && `s1: ${row.pubidsub1}`,
                                            row.pubidsub2 && `s2: ${row.pubidsub2}`,
                                            row.pubidsub3 && `s3: ${row.pubidsub3}`,
                                            row.pubidsub4 && `s4: ${row.pubidsub4}`,
                                            row.pubidsub5 && `s5: ${row.pubidsub5}`,
                                        ].filter(Boolean);
                                        return (
                                            <tr key={row.id} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 font-mono text-xs max-w-[150px] truncate" title={row.transaction_id}>{row.transaction_id}</td>
                                                <td className="px-4 py-3">{row.advertiser_name}</td>
                                                <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{row.network_id || '—'}</span></td>
                                                <td className="px-4 py-3 font-bold uppercase">{row.geo}</td>
                                                <td className="px-4 py-3 text-emerald-600 font-semibold">kr {(row.revenue / 100).toFixed(2)}</td>
                                                <td className="px-4 py-3 max-w-[200px]">
                                                    {subIds.length > 0 ? (
                                                        <div className="flex flex-wrap gap-1">
                                                            {subIds.map((s, j) => (
                                                                <span key={j} className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded text-xs font-mono">{s}</span>
                                                            ))}
                                                        </div>
                                                    ) : <span className="text-gray-400 text-xs">None</span>}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {isEditing ? (
                                                        <div className="flex items-center gap-1">
                                                            <input
                                                                type="text"
                                                                placeholder="Lead ID"
                                                                className="border rounded px-2 py-1 text-xs w-24"
                                                                value={unmatchedLeadId}
                                                                onChange={(e) => setUnmatchedLeadId(e.target.value)}
                                                            />
                                                            <button
                                                                onClick={async () => {
                                                                    if (!unmatchedLeadId) return;
                                                                    try {
                                                                        await dashboardApi.assignUnmatched(row.id, unmatchedLeadId);
                                                                        setEditingUnmatched(null);
                                                                        setUnmatchedLeadId('');
                                                                        fetchData();
                                                                    } catch (e) { alert('Error assigning'); }
                                                                }}
                                                                className="bg-indigo-600 text-white px-2 py-1 rounded text-xs font-semibold hover:bg-indigo-700"
                                                            >Save</button>
                                                        </div>
                                                    ) : (
                                                        <button onClick={() => { setEditingUnmatched(row.id); setUnmatchedLeadId(row.pubidsub2 || ''); }} className="text-indigo-600 font-semibold text-xs hover:underline">Assign</button>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <button
                                                        onClick={async () => {
                                                            try {
                                                                await dashboardApi.dismissUnmatched(row.id);
                                                                fetchData();
                                                            } catch (e) { alert('Error dismissing'); }
                                                        }}
                                                        className="text-gray-400 hover:text-red-500 text-xs font-semibold"
                                                    >Dismiss</button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Management: Categories Table */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mt-6">
                    <div className="bg-slate-50 border-b border-gray-100 px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <Tag className="text-indigo-600 w-5 h-5 flex-shrink-0" />
                            <h3 className="text-lg font-bold text-gray-900">Manage Categories</h3>
                        </div>

                        <div className="flex gap-2 items-center w-full sm:w-auto">
                            <input
                                type="text"
                                placeholder="New Category Name..."
                                className="text-sm border-gray-300 rounded-lg p-2 border focus:ring-indigo-500 focus:border-indigo-500 shadow-sm flex-1 sm:flex-none"
                                value={newCategoryName}
                                onChange={(e) => setNewCategoryName(e.target.value)}
                            />
                            <button
                                onClick={handleCreateCategory}
                                className="bg-indigo-600 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700 transition flex items-center gap-1 flex-shrink-0"
                            >
                                <Plus size={16} /> Create
                            </button>
                        </div>
                    </div>
                    <div className="p-6 flex gap-2 flex-wrap">
                        {categories.map(c => (
                            <span key={c.id} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800 border border-gray-200">
                                {c.name}
                            </span>
                        ))}
                        {categories.length === 0 && !loading && (
                            <span className="text-gray-400 text-sm italic">No categories created yet. Create one to assign advertisers.</span>
                        )}
                    </div>
                </div>

            </div>
        </MainLayout>
    );
}

// Subcomponents
function KpiCard({ title, value, icon, loading }: any) {
    return (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex items-start justify-between hover:shadow-md transition-shadow relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity transform group-hover:scale-110">
                <div className="w-16 h-16">{icon}</div>
            </div>
            <div className="z-10 relative">
                <p className="text-sm font-medium text-gray-500 flex items-center gap-2 mb-2">
                    {title}
                </p>
                <h3 className="text-3xl font-bold text-gray-900 tracking-tight font-display">
                    {loading ? <div className="h-8 w-24 bg-gray-200 rounded animate-pulse" /> : value}
                </h3>
            </div>
            <div className="p-3 bg-indigo-50 rounded-xl text-indigo-600 z-10 relative">
                {icon}
            </div>
        </div>
    );
}

function Skeleton() {
    return <div className="w-full h-full bg-slate-100 rounded-xl animate-pulse"></div>;
}
